// RITA-1525 — Alle Seiten eines Dokuments nach der berechneten Seitenaufteilung.
// In der Bearbeitung ist dieser Container der gemeinsame contentEditable-Host aller Seiten.
import React from 'react';
import { Seitengeometrie } from '../layout/seitengeometrie';
import { Seiteninhalt } from '../layout/seitenAufteilen';
import { Wertemodus } from '../model/platzhalterwerte';
import { Vertragsdokument } from '../model/types';
import VertragSeite, { Bearbeitungsstand } from './VertragSeite';

interface Props {
  dokument: Vertragsdokument;
  seiten: Seiteninhalt[];
  modus: Wertemodus;
  geometrie: Seitengeometrie;
  /** Kennzeichnet die Druckansicht im Portal, damit Tests beide Ansichten unterscheiden können. */
  druck?: boolean;
  /** Bearbeitung: Host-Attribute und Bereichszustand; ohne Angabe reine Ansicht. */
  bearbeitung?: Bearbeitungsstand;
  hostRef?: React.Ref<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  gesperrt?: boolean;
}

function VertragSeiten({
  dokument,
  seiten,
  modus,
  geometrie,
  druck = false,
  bearbeitung,
  hostRef,
  onMouseDown,
  onClick,
  gesperrt = false,
}: Props) {
  const bearbeitbar = !!bearbeitung;
  return (
    // Der Host ist in der Bearbeitung ein contentEditable-Textfeld (role textbox); Tastatur- und
    // Eingabeereignisse hängen als native Listener am Host (editor/VertragEditor.tsx), Klicks und
    // mousedown werten nur Inseln aus (Kästchen, Umbruch entfernen, Kopf-/Fußzeile aktivieren).
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={bearbeitbar ? 'vertrag-seiten vertrag-bearbeitung' : 'vertrag-seiten'}
      data-testid={druck ? 'vertrag-druck-seiten' : 'vertrag-seiten'}
      data-seitenzahl={seiten.length}
      data-bereich-aktiv={bearbeitung?.bereich}
      ref={hostRef}
      onMouseDown={onMouseDown}
      onClick={onClick}
      contentEditable={bearbeitbar ? !gesperrt : undefined}
      suppressContentEditableWarning={bearbeitbar ? true : undefined}
      spellCheck={bearbeitbar ? true : undefined}
      role={bearbeitbar ? 'textbox' : undefined}
      aria-multiline={bearbeitbar ? true : undefined}
      aria-label={bearbeitbar ? 'Vertragstext' : undefined}
      aria-disabled={bearbeitbar && gesperrt ? true : undefined}
      aria-readonly={bearbeitbar && gesperrt ? true : undefined}
    >
      {seiten.map((seite, index) => (
        // Bewusst Inhalts- statt Index-Keys: Eine Seite, deren Blockmenge sich ändert, wird komplett neu
        // aufgebaut, unveränderte Seiten bleiben stehen. Die Auswahl wird ohnehin aus dem Modell
        // wiederhergestellt; renderVersion erzwingt den Neuaufbau nach einer Komposition.
        <VertragSeite
          key={`${seite.blockIds.join('|') || `leer-${index + 1}`}@${bearbeitung?.renderVersion ?? 0}`}
          nummer={index + 1}
          gesamt={seiten.length}
          dokument={dokument}
          blockIds={seite.blockIds}
          ueberlauf={seite.ueberlauf}
          modus={modus}
          geometrie={geometrie}
          bearbeitung={bearbeitung}
          gesperrt={gesperrt}
        />
      ))}
    </div>
  );
}

export default VertragSeiten;
